import React, { useState, useEffect } from 'react'
import { Typography, Box, Checkbox, Chip, Accordion, AccordionSummary, AccordionDetails, FormGroup, FormControlLabel } from '@mui/material';
import { Close, Add, ExpandLess } from '@mui/icons-material'
import CustomButton from '../Common/CustomButton';

import "../../styles/components/TestScenarios/TestRulesSuggestions.scss";
import * as monaco from 'monaco-editor';
import MonacoEditorWithFullscreen from 'components/Common/MonacoEditorWithFullscreen';
import { useTheme } from 'contexts/ThemeContext';


// Define custom themes for JavaScript
monaco.editor.defineTheme("js-theme-light", {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '0000FF' },      // Blue for keywords
    { token: 'string', foreground: 'A31515' },       // Red for strings
    { token: 'number', foreground: '098658' },       // Green for numbers
    { token: 'comment', foreground: '008000' },      // Green for comments
    { token: 'function', foreground: '795E26' },     // Brown for functions
    { token: 'variable', foreground: '001080' },     // Dark blue for variables
    { token: 'operator', foreground: '000000' },     // Black for operators
    { token: 'delimiter', foreground: '000000' },    // Black for delimiters
    { token: 'bracket', foreground: '000000' },      // Black for brackets
  ],
  colors: {
    'editor.background': '#ffffff',
    'editor.foreground': '#000000',
    'editorLineNumber.foreground': '#2B91AF',
    'editor.lineHighlightBackground': '#f0f0f0',
    'editor.lineHighlightBorder': '#f0f0f0',
    'editorGutter.background': '#f3f3f3',
    'editor.selectionBackground': '#add6ff',
    'editor.inactiveSelectionBackground': '#e5ebf1',
    'editorCursor.foreground': '#000000',
    'editorWhitespace.foreground': '#A0A0A0',
    'editorIndentGuide.background': '#D3D3D3',
    'editorIndentGuide.activeBackground': '#939393',
    'editorLineNumber.activeForeground': '#000000',
    'editor.selectionHighlightBackground': '#add6ff',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});

monaco.editor.defineTheme("js-theme-dark", {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569CD6' },      // Light blue for keywords
    { token: 'string', foreground: 'CE9178' },       // Light red for strings
    { token: 'number', foreground: 'B5CEA8' },       // Light green for numbers
    { token: 'comment', foreground: '6A9955' },      // Light green for comments
    { token: 'function', foreground: 'DCDCAA' },     // Light yellow for functions
    { token: 'variable', foreground: '9CDCFE' },     // Light blue for variables
    { token: 'operator', foreground: 'D4D4D4' },     // Light gray for operators
    { token: 'delimiter', foreground: 'D4D4D4' },    // Light gray for delimiters
    { token: 'bracket', foreground: 'D4D4D4' },      // Light gray for brackets
  ],
  colors: {
    'editor.background': '#1E1E1E',
    'editor.foreground': '#D4D4D4',
    'editorLineNumber.foreground': '#858585',
    'editor.lineHighlightBackground': '#2A2D2E',
    'editor.lineHighlightBorder': '#2A2D2E',
    'editorGutter.background': '#1E1E1E',
    'editor.selectionBackground': '#264F78',
    'editor.inactiveSelectionBackground': '#3A3D41',
    'editorCursor.foreground': '#FFFFFF',
    'editorWhitespace.foreground': '#404040',
    'editorIndentGuide.background': '#404040',
    'editorIndentGuide.activeBackground': '#707070',
    'editorLineNumber.activeForeground': '#FFFFFF',
    'editor.selectionHighlightBackground': '#264F78',
    'editor.wordHighlightBackground': '#575757',
    'editor.wordHighlightStrongBackground': '#004972',
  }
});


const TestRulesSuggestions = ({ testRules, saveTestRules, setAlert, onCancel }) => {
  const [checkedIds, setCheckedIds] = useState([]);
  const [expandedId, setExpandedId] = useState("");
  const { isDarkMode } = useTheme();

  const handleChange = (id, value) => {
    setCheckedIds(prevState => {
      const index = prevState.indexOf(id);
      if (value) return [...checkedIds, id];
      return [...prevState.slice(0, index), ...prevState.slice(index + 1, prevState.length)];
    })
  }

  const clearSelection = () => {
    setCheckedIds([]);
    if (!checkedIds.length)
      onCancel();
  }

  const onSave = () => {
    saveTestRules(checkedIds);
    setCheckedIds([]);
  }

  const onExpand = (ruleId) => {
    let newValue = ruleId;
    if (ruleId === expandedId) newValue = "";
    setExpandedId(newValue);
  }

  const renderSingleRule = (rule) => {
    return (
      <Accordion expanded={rule.id === expandedId}
        sx={{
          '.MuiAccordionSummary-content': {
            width: '100%'
          },
          backgroundColor: 'var(--background-secondary) !important', border: "1px solid var(--borderColor)"
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandLess />}
          aria-controls="suggested-testrule-content"
          id={rule.id}
          onClick={() => { onExpand(rule.id) }}
        >
          <div style={{ width: "100%" }} className="title">
            <Checkbox onClick={(event) => { event.stopPropagation(); }} onChange={(event, value) => { handleChange(rule.id, value) }} checked={checkedIds.indexOf(rule.id) !== -1} />
            <div className="truncate-1-lines" style={{ width: "calc(100% - 180px)", display: "inline-block", whiteSpace: "pre", verticalAlign: "middle" }}>{rule?.title}</div>
            <Chip label="Suggested by AI" className="suggestedTag" />
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ border: "1px solid var(--borderColor)", borderRadius: "4px", p: 1 }}>
            <Typography sx={{ mb: "4px", fontWeight: 550, position: "relative" }}>
              function () {"{"}
            </Typography>
            <MonacoEditorWithFullscreen
              className="jsEditor"
              language="javascript"
              theme={isDarkMode ? "js-theme-dark" : "js-theme-light"}
              value={rule.expression}
              height="150px"
              style={{ borderBottom: "1px solid var(--borderColor)" }}
              options={{
                automaticLayout: true,
                wordWrap: "on",
                minimap: { enabled: false },
                lineNumbers: "off",
                tabSize: 4,
                overviewRulerLanes: 0,
                readOnly: true
              }}
            />
            <Typography sx={{ fontWeight: 550 }}>{"}"}</Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
    )
  }

  const renderRules = () => {
    if (!testRules || testRules.length === 0) {
      return (
        <Box style={{ padding: '16px 0px' }}>
          No Suggested Rules Found
        </Box>
      )
    }
    return (
      testRules.map((rule) => {
        return (
          <div key={rule.id} style={{ margin: "8px 0" }}>
            {renderSingleRule(rule)}
          </div>
        )
      })
    )
  }
  return (
    <Box sx={{ my: 1 }} className="testRulesSuggestions">
      {renderRules()}
      <CustomButton startIcon={<Add />} disabled={checkedIds.length === 0} sx={{ mr: 1 }} onClick={onSave}>Save</CustomButton>
      <CustomButton startIcon={<Close />} onClick={clearSelection}>{checkedIds.length ? "Clear Selection" : "Cancel"}</CustomButton>
    </Box>
  )
}

export default TestRulesSuggestions
